import { get, isEmpty } from "lodash";
import {
    BACK_CAMERA_LABEl_LIST,
    CAMERA_FACE,
    CAMERA_FACING_MODE,
    DEFAULT_VIDEO_QUALITY,
    FRNOT_CAMERA_LABEL_LIST,
    MEDIA_DEVICE_PERMISSION_QUERY_TYPE,
    MEDIA_PERMISSIONS,
    QUALITY_TYPE_MAP,
    VIDEO_QUALITY_LIST,
} from "../../../../constants/CallSettingConstants";
import reduxStore from "../../../../configureEnvStore";
import {
    setCurrentSelectedVideoQuality,
    setVideoQualityResolutionParams,
    setVideoQualityResolutionList,
    setCurrentCameraDevice,
    setIsFrontCameraSelected,
    setCurrentAudioDevice,
    setCurrentSpeakerDevice,
    setCameraPermission,
    setAudioPermission,
    setCameraDevices,
    setWorkingCam,
    setIsStartCallSettingLoader,
} from "../../../../reducers/call-setting-reducer";
import { CALL_DATA, GLOBAL_MEETING_DATA, PublicVariables } from "../../../../constants/PublicVariables";
import { STATUS } from "../../../../constants/LivePointerConstants";
import mediaDevice from "../../../../services/mediaDeviceService";
import { checkDeviceHasCamIssue, dualStreamEnabled, isDeviceTypeAndroidBrowser, isDeviceTypeIOSBrowser } from "../../../../shared/utility";
import { CALL_BODY_SCREEN, DISABLE, ENABLED } from "../../../../constants/Constants";
import { LogFirebase } from "../../../../services/firestore";
import { NOTIFICATION_TYPES } from "../../../../constants/DataTrackNotificationConstants";
import { sendDataTrack, sendMsgForPointer } from "../../../../shared/dataTrackFunction";
import livePointerService from "../../../LivePointer/livePointerService";
import flashService from "../../../../services/flashService";
import { isSilentObserverAvailable } from "../../../../shared/function";
import {
    addVideoParams,
    checkIsVideoPermissionBlock,
    publishVideo,
    unpublishVideo,
    videoInputDeviceChange,
} from "../../../Twilio/VideoCall/VideoUtils";
import { manageNewLocalAudiotracks, micButtonLDFlagStatus } from "../../../Twilio/AudioCall/AudioCallUtils";
import { restartLocalVideoTracks, videoTracksExist } from "../../../Twilio/twilioCallUtility";
import { getLocalVideoTrack, getRoom } from "../../../Twilio/Room/roomService";
import { handleErrorForVideo } from "../../../Twilio/Room/roomFunction";

const initializeCameraForMobile = () => {
    const { isRoomConnected } = reduxStore.getState().commonReducer;
    if ((PublicVariables.featureFlag.SHOWMEVIDEO == CAMERA_FACE.FRONT && isRoomConnected) || checkDeviceHasCamIssue()) {
        changeSelectedCamera(CAMERA_FACING_MODE.FRONT);
    } else {
        changeSelectedCamera(CAMERA_FACING_MODE.BACK);
    }
};

// it will set mediaDevices to the local states
export const setAllMediaDevices = async () => {
    const res = await mediaDevice.getMediaDevices();
    const { currentCameraDevice, currentAudioDevice, currentSpeakerDevice } = reduxStore.getState().callSetting;

    if (res.camera.length > 0 && isEmpty(currentCameraDevice.deviceId)) {
        if (PublicVariables.isMobile) {
            initializeCameraForMobile();
        } else {
            reduxStore.dispatch(setCurrentCameraDevice(res.camera[0]));
        }
    }

    if (res.audio.length > 0 && isEmpty(currentAudioDevice.deviceId)) {
        reduxStore.dispatch(setCurrentAudioDevice(res.audio[0]));
    }

    if (res.speaker.length > 0 && isEmpty(currentSpeakerDevice.deviceId)) {
        reduxStore.dispatch(setCurrentSpeakerDevice(res.speaker[0]));
    }
};

export const assignCameraDevice = (facingMode) => {
    reduxStore.dispatch(setIsFrontCameraSelected(facingMode === CAMERA_FACING_MODE.FRONT));
    const cameraLabelArrayList = facingMode === CAMERA_FACING_MODE.FRONT ? FRNOT_CAMERA_LABEL_LIST : BACK_CAMERA_LABEl_LIST;
    const cameraDevicesList = reduxStore.getState().callSetting.cameraDevices;
    const currentCameraDevice = reduxStore.getState().callSetting.currentCameraDevice;

    if (!isEmpty(cameraDevicesList)) {
        let selectedNewCameraDevice = currentCameraDevice;
        cameraDevicesList.forEach((device) => {
            cameraLabelArrayList.forEach((value) => {
                if (device.label.toLowerCase().indexOf(value.name) > -1) {
                    if (PublicVariables.isIOSDevice) {
                        if (device.label === "back camera" || device.label === "Back Camera") {
                            selectedNewCameraDevice = device;
                        }
                        if (device.label === "front camera" || device.label === "Front Camera") {
                            selectedNewCameraDevice = device;
                        }
                    } else {
                        selectedNewCameraDevice = device;
                    }
                }
            });
        });

        if (isEmpty(selectedNewCameraDevice.deviceId)) {
            selectedNewCameraDevice = cameraDevicesList[0];
        }
        reduxStore.dispatch(setCurrentCameraDevice(selectedNewCameraDevice));
        return selectedNewCameraDevice;
    }
};

/**set camera in call setting pop up as per call experience settings/Admin */
export const setCameraByCallExperience = () => {
    if (PublicVariables.featureFlag.SHOWMEVIDEO == CAMERA_FACE.FRONT) {
        changeSelectedCamera(CAMERA_FACING_MODE.FRONT);
    } else if (PublicVariables.featureFlag.SHOWMEVIDEO == CAMERA_FACE.BACK) {
        changeSelectedCamera(CAMERA_FACING_MODE.BACK);
    }
};

// change selected camera whichever set from flip camera icon, facingMode = user/environment
export const changeSelectedCamera = (facingMode) => {
    return assignCameraDevice(facingMode);
};

export const addRemoveAutoWidthClass = (isAdd) => {
    if (isAdd) {
        document.getElementById("call_body")?.classList.add("call-video-auto-width");
    } else {
        document.getElementById("call_body")?.classList.remove("call-video-auto-width");
    }
};

const getQualityKey = (qualityMap, resolution) => {
    for (const key in qualityMap) {
        if (qualityMap[key] === resolution) {
            return key; // Return the matching key
        }
    }
    return undefined; // Return undefined if no match is found
};

// Sets resolution values for web/mweb, to be used in creating video and call connect.
export const setVideoQualityResolution = (joinMeetingData) => {
    // Parse the response resolution list from the joinMeetingData
    let responseResolutionList = get(joinMeetingData, "resolution", {});
    responseResolutionList = JSON.parse(responseResolutionList);

    // Get the desired video quality from session configurations or use the default
    let showMeVideoQualityResolution = get(joinMeetingData, "sessionConfigurations.SHOWMERESOLUTION", DEFAULT_VIDEO_QUALITY.value);

    // Custom Mapping For SHOWMERESOLUTION feature key
    if (Object.keys(QUALITY_TYPE_MAP).includes(showMeVideoQualityResolution)) {
        showMeVideoQualityResolution = QUALITY_TYPE_MAP[showMeVideoQualityResolution];
    }

    // Determine the resolution based on whether it's a mobile or web environment
    const resolution = PublicVariables.isMobile ? responseResolutionList.MWEB : responseResolutionList.WEB;

    reduxStore.dispatch(setVideoQualityResolutionList(resolution));

    // Check if the selected video quality exists in the resolution list and dispatch its parameters
    const quality = getQualityKey(QUALITY_TYPE_MAP, showMeVideoQualityResolution);
    if (resolution?.hasOwnProperty(quality)) {
        const selectedResolutionParams = resolution[quality];
        reduxStore.dispatch(setVideoQualityResolutionParams(selectedResolutionParams));
    }

    showMeVideoQualityResolution = VIDEO_QUALITY_LIST.find((videoQuality) => videoQuality.value === showMeVideoQualityResolution);
    reduxStore.dispatch(setCurrentSelectedVideoQuality(showMeVideoQualityResolution));
};

// automatically starts guest's video on call join time
export const videoOnCallJoinFeature = async () => {
    const { startLocalVideo, isLocalUserScreenSharing: isScreenSharing } = reduxStore.getState().twilioEvents;
    const isAnyVideoAvailable = videoTracksExist();
    const room = getRoom();
    console.log("room_participants_size", room.participants.size);
    if (
        PublicVariables.featureFlag.SHOWMEDUALSTREAM == DISABLE &&
        !isAnyVideoAvailable &&
        !startLocalVideo && //single stream allowed
        room.participants.size < 2 &&
        !isScreenSharing &&
        !isSilentObserverAvailable() // for the silent observer do not publish video
    ) {
        if (checkIsVideoPermissionBlock()) {
            return;
        }
        LogFirebase("Video", "Auto_start", "true");
        publishVideo("joinCall");
        return;
    }
    if (
        PublicVariables.featureFlag.SHOWMEDUALSTREAM == ENABLED &&
        !startLocalVideo &&
        !isScreenSharing && //multiple stream allowed
        !isSilentObserverAvailable() // for the silent observer do not publish video
    ) {
        if (checkIsVideoPermissionBlock()) {
            return;
        }
        setTimeout(() => {
            LogFirebase("Video", "Auto_start", "true");
            publishVideo("joinCall");
        }, 500);
    }
};

// automatically starts audio on call join time
export const manageShowMeMicrophone = () => {
    const { startLocalAudio } = reduxStore.getState().twilioEvents;
    if (
        !startLocalAudio &&
        PublicVariables.featureFlag?.SHOWMEMICROPHONE === ENABLED &&
        !PublicVariables.onJoinCallAudio &&
        micButtonLDFlagStatus(PublicVariables.ldFlags?.micButtonStatus)
    ) {
        PublicVariables.onJoinCallAudio = true;
        setTimeout(() => {
            LogFirebase("Audio", "Auto_start", "true");
            manageNewLocalAudiotracks();
        }, 100);
    }
};

const handleDeviceHasCamIssue = () => {
    //old flow for note 10+ & S20 plus and Android 11
    unpublishVideo();
    publishVideo().then(function () {
        const localVideoTrack = getLocalVideoTrack();
        localVideoTrack.once("started", () => {
            //dimensions available only after track started
            if (PublicVariables.ldFlags?.captureDeviceAndGoogleEvent && localVideoTrack?.dimensions) {
                LogFirebase("VideoQuality", "changedVideoTrackWidth", localVideoTrack?.dimensions?.width);
                LogFirebase("VideoQuality", "changedVideoTrackHeight", localVideoTrack?.dimensions?.height);
            }
        });
    });
};

const handleDeviceNotHaveCamIssue = () => {
    restartLocalVideoTracks(addVideoParams()).then(() => {
        const localVideoTrack = getLocalVideoTrack();

        // //flash gets turn off when tracks restarted, so change the status of buttons local and agents both
        if (!dualStreamEnabled() && (isDeviceTypeAndroidBrowser() || isDeviceTypeIOSBrowser())) {
            flashService.handleFlashTrack();
        }

        //dimensions available only after track started
        if (PublicVariables.ldFlags?.captureDeviceAndGoogleEvent && localVideoTrack?.dimensions) {
            LogFirebase("VideoQuality", "changedVideoTrackWidth", localVideoTrack?.dimensions?.width);
            LogFirebase("VideoQuality", "changedVideoTrackHeight", localVideoTrack?.dimensions?.height);
        }

        // Re-enable and auto-start live pointer after video quality change
        // restartLocalVideoTracks doesn't trigger handleStartLocalVideoTrackForSingleStream, so handle it here
        if (!dualStreamEnabled()) {
            sendMsgForPointer(STATUS.ENABLED);
            livePointerService.autoStartLivePointer("after_video_quality_change");
        }
    });
};

const sendTrackOnVideoQualityChange = (resolutionKey) => {
    // sends message to connected participants in the call to change video quality
    if (GLOBAL_MEETING_DATA.isAgent) {
        LogFirebase("VideoQuality", "changedByAgent", resolutionKey);
        sendVideoQualityDataTrack(resolutionKey);
    } else {
        LogFirebase("VideoQuality", "change_received", resolutionKey);
    }
};

// changes video quality as per users choice function can not be called when expanded/pinned view ON - call settings button is hidden
export const changeVideoQuality = (resolutionKey, fromStartCamera = false) => {
    const { videoQualityResolutionList, currentSelectedVideoQuality } = reduxStore.getState().callSetting;
    const { startLocalVideo } = reduxStore.getState().twilioEvents;
    const { pointerOn } = reduxStore.getState().livePointer;

    if (currentSelectedVideoQuality.value === resolutionKey) return;

    const updatedVideoQuality = VIDEO_QUALITY_LIST.find((quality) => quality.value === resolutionKey);
    reduxStore.dispatch(setCurrentSelectedVideoQuality(updatedVideoQuality));

    const quality = getQualityKey(QUALITY_TYPE_MAP, resolutionKey);
    const selectedResolutionParams = videoQualityResolutionList[quality];
    reduxStore.dispatch(setVideoQualityResolutionParams(selectedResolutionParams));

    if (startLocalVideo) {
        if (pointerOn) {
            //applicable to single stream
            livePointerService.turnOffLivePointer();
            sendMsgForPointer(STATUS.OFF, "0"); // showtoast = "0"
        }

        if (checkDeviceHasCamIssue()) {
            handleDeviceHasCamIssue();
        } else {
            handleDeviceNotHaveCamIssue();
        }
    }

    sendTrackOnVideoQualityChange(resolutionKey);
};

// checks onload and if permission is granted then stores device list
export const checkCallSettingPermissionsOnLoad = async () => {
    mediaDevice.unsetCamLoading();
    const audioPermission = await mediaDevice.checkPermission(MEDIA_DEVICE_PERMISSION_QUERY_TYPE.MIC);
    const cameraPermission = await mediaDevice.checkPermission(MEDIA_DEVICE_PERMISSION_QUERY_TYPE.CAMERA);

    if (cameraPermission === MEDIA_PERMISSIONS.GRANTED || audioPermission === MEDIA_PERMISSIONS.GRANTED) {
        await mediaDevice.getMediaDevices(); // fetch all the device list
    }

    reduxStore.dispatch(setCameraPermission(cameraPermission));
    reduxStore.dispatch(setAudioPermission(audioPermission));
};

export const showHideCallSettingButton = () => {
    const { expandView, currentCallBodyScreen, isRoomConnected } = reduxStore.getState().commonReducer;
    const uploadFilesView = [CALL_BODY_SCREEN.NOTIFY_SCREEN, CALL_BODY_SCREEN.UPLOAD_FILE_SCREEN].includes(currentCallBodyScreen);

    return !((PublicVariables.featureFlag.SHOWMEDUALSTREAM == ENABLED && expandView && isRoomConnected) || uploadFilesView);
};

// call setting button functionality available
export const isCallSettingAvailable = () => {
    const { expandView, isRemoteParticipantJoined } = reduxStore.getState().commonReducer;

    if (isRemoteParticipantJoined && dualStreamEnabled()) {
        if (!expandView) {
            return true;
        } else if (expandView) {
            return false;
        } else {
            return true;
        }
    } else {
        return true;
    }
};

export const sendVideoQualityDataTrack = (qualityKey) => {
    const { currentSelectedVideoQuality } = reduxStore.getState().callSetting;
    const resolutionKey = qualityKey || currentSelectedVideoQuality.value;

    const msg = {
        notification_type: NOTIFICATION_TYPES.VIDEO_QUALITY,
        video_quality: resolutionKey,
        isAgent: true,
        identity: CALL_DATA.localIdentity,
        device_type: GLOBAL_MEETING_DATA.device_type,
    };

    sendDataTrack(msg);
};

export const getWorkingCamera = () => {
    return new Promise((resolve, reject) => {
        let camPermission = reduxStore.getState().callSetting.cameraPermission || MEDIA_PERMISSIONS.PROMPT;
        let workingCam = false;
        const { currentCameraDevice, cameraDevices } = reduxStore.getState().callSetting;
        if (currentCameraDevice && cameraDevices?.length > 0) {
            workingCam = cameraDevices[cameraDevices.length - 1];
            resolve(workingCam);
        } else {
            if (camPermission != MEDIA_PERMISSIONS.BLOCKED) {
                const constraints = { video: true };
                navigator.mediaDevices.getUserMedia(constraints).then(
                    function (stream) {
                        navigator.mediaDevices.enumerateDevices().then(function (allMediaDevices) {
                            const res = mediaDevice.categorizeMediaDevices(allMediaDevices);
                            console.log(res, "res____");
                            reduxStore.dispatch(setCameraDevices(res.camera));
                            workingCam = res.camera[res.camera.length - 1];
                            console.log(workingCam, "workingCam___");
                            if (stream) {
                                stream.getTracks().forEach((track) => {
                                    track.stop();
                                });
                            }
                            resolve(workingCam);
                        });
                    },
                    function (error) {
                        handleErrorForVideo(error);
                    }
                );
            }
        }
    });
};

export const attachSinkId = (sinkId, isCallNotConnected) => {
    let targetElement = null;
    let audioElement = document.getElementById("audioTag").querySelector("audio");
    if (audioElement) {
        targetElement = audioElement;
    } else if (!isCallNotConnected) {
        // call connected
        const { expandView } = reduxStore.getState().commonReducer;
        if (!expandView) {
            //need to handle vdo from grid view
            targetElement = document.getElementById("galleryVideo_" + CALL_DATA.localIdentity).querySelector("video");
        } else {
            targetElement = document.getElementById("expandedVideo").querySelector("video");
        }
    } else {
        targetElement = document.getElementById("callsettingVideo"); // call settings video element
        targetElement = document.getElementById("callsettingVideo_vb"); // call settings VB video element
    }
    if (typeof targetElement?.sinkId !== "undefined") {
        console.error("sinkID : " + targetElement.sinkId);
        targetElement
            .setSinkId(sinkId)
            .then(() => {
                console.log(`Success, audio output device attached: ${sinkId}`);
            })
            .catch((error) => {
                let errorMessage = error;
                if (error.name === "SecurityError") {
                    errorMessage = `You need to use HTTPS for selecting audio output device: ${error}`;
                }
                console.error(errorMessage);
            });
    } else {
        console.warn("Browser does not support output device selection.");
    }
};

export const changeVideoInput = () => {
    if (!checkDeviceHasCamIssue()) {
        //------ for safari just restart video tracks with params -----
        reduxStore.dispatch(setWorkingCam(false));
        restartLocalVideoTracks(addVideoParams()).then(function () {
            mediaDevice.hideCallSettingCamLoading();
            //flash gets turn off when tracks restarted, so change the status of buttons local and agents both
            if (!dualStreamEnabled() && (isDeviceTypeAndroidBrowser() || isDeviceTypeIOSBrowser())) {
                flashService.handleFlashTrack();
            }
        });
    } else {
        videoInputDeviceChange();
    }
};

export const showCallSettingLoadingView = () => {
    reduxStore.dispatch(setIsStartCallSettingLoader(true));
    const callSettingLoaderDiv = document.getElementById("call_setting_loader_start");
    const callSettingLoaderEndDiv = document.getElementById("call_setting_loader_end");
    if (callSettingLoaderEndDiv) {
        callSettingLoaderEndDiv.style.display = "none";
    }
    if (callSettingLoaderDiv) {
        callSettingLoaderDiv.style.display = "flex";
    }
};

export const showCallSettingView = () => {
    reduxStore.dispatch(setIsStartCallSettingLoader(false));
    const callSettingLoaderDiv = document.getElementById("call_setting_loader_start");
    const callSettingLoaderEndDiv = document.getElementById("call_setting_loader_end");
    if (callSettingLoaderEndDiv) {
        callSettingLoaderEndDiv.style.display = "flex";
    }
    if (callSettingLoaderDiv) {
        callSettingLoaderDiv.style.display = "none";
    }
};
